/**
 * OrganizationsList Page
 *
 * Platform Management: CRUD operations for organizations
 * Superadmin only - manage all customer organizations
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  BuildingOfficeIcon,
  PlusIcon,
  PencilIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  TrashIcon,
  ArrowPathIcon,
  LinkIcon,
  CreditCardIcon,
  PlayIcon,
  PowerIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { DataTable, ColumnDefinition, ActionDefinition, PaginationState, SortState } from '@/components/shared/DataTable';
import { Crown, Building2, Search } from 'lucide-react';
import { FilterBar, FilterConfig } from '@/components/shared/FilterBar';
import { useFilterParams } from '@/hooks/useFilterParams';
import { BulkActionsBar, BulkAction } from '@/components/shared/BulkActionsBar';
import { OrganizationModal, Organization } from '../components/management/OrganizationModal';
import { useAuth } from '@/hooks/useAuth';
import { canUseConsoleAction, type ConsoleAction } from '@/utils/consoleActionAccess';
import { useOrganizationFeatureFlags } from '@/stores/organizationFeatureFlagsStore';
import { BulkUserImportModal } from '../components/management/BulkUserImportModal';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import { ManageSubscriptionModal } from '../components/subscriptions/ManageSubscriptionModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DeactivateConfirmModal, CascadeImpact } from '../components/deletion';
import { API_BASE_URL } from '../config/api';

// Origin without the /api suffix API_BASE_URL carries; the fetch calls below build their own
// /api/v1 paths. Read it from the shared config, not import.meta, so this module loads
// outside Vite.
const API_URL = API_BASE_URL.replace(/\/api$/, '');

// Partner type for filters and display
interface Partner {
  id: string;
  name: string;
  slug: string;
  type: 'reseller' | 'affiliate' | 'enterprise' | 'internal';
  status: string;
  isOwnerPartner?: boolean;
  organizationCount?: number;
  quotaUsage?: {
    organizations: number;
    maxOrganizations: number;
    isUnlimited: boolean;
  };
}

const OrganizationsList: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);

  // Use URL-synced filter params
  // Without an explicit key set the hook infers one from `formatters` alone and rejects `search`.
  const { filters, setFilter, clearFilter, clearAllFilters, activeFilters } = useFilterParams<{
    search: string;
    partner: string;
    orgStatus: string;
  }>({
    schema: {
      search: { type: 'string', default: '' },
      // Empty is the no-filter value FilterSelect's placeholder option carries.
      partner: { type: 'string', default: '' },
      orgStatus: { type: 'string', default: 'active' },
    },
    labels: { search: 'Search', partner: 'Partner', orgStatus: 'Status' },
    formatters: {
      partner: (value) => partners.find((p) => p.id === value)?.name ?? String(value),
      orgStatus: (value) => (value === 'deactivated' ? 'Deactivated' : 'Active'),
    },
  });

  // Pagination and sorting state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [sortState, setSortState] = useState<SortState>({
    column: 'name',
    direction: 'asc',
  });

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal states
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);

  // Deactivate/Reactivate state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatingOrg, setDeactivatingOrg] = useState<Organization | null>(null);
  const [cascadeImpact, setCascadeImpact] = useState<CascadeImpact | undefined>(undefined);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [reactivatingOrg, setReactivatingOrg] = useState<Organization | null>(null);

  // Hard delete state
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [hardDeletingOrg, setHardDeletingOrg] = useState<Organization | null>(null);
  const [hardDeleteConfirmName, setHardDeleteConfirmName] = useState('');
  const [hardDeleteCheckbox, setHardDeleteCheckbox] = useState(false);
  const [isHardDeleting, setIsHardDeleting] = useState(false);

  // Assign partner modal state
  const [showAssignPartnerModal, setShowAssignPartnerModal] = useState(false);
  const [assigningOrg, setAssigningOrg] = useState<Organization | null>(null);
  const [partnerSearchQuery, setPartnerSearchQuery] = useState('');
  const [partnerSearchResults, setPartnerSearchResults] = useState<Partner[]>([]);
  const [isSearchingPartners, setIsSearchingPartners] = useState(false);
  const [isAssigningPartner, setIsAssigningPartner] = useState(false);

  // Manage subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionOrg, setSubscriptionOrg] = useState<Organization | null>(null);

  // Bulk user import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importingOrg, setImportingOrg] = useState<Organization | null>(null);

  const canViewPartners = hasPermission('platform.partners.view');

  // Load partners for filter dropdown
  const fetchPartners = useCallback(async () => {
    if (!canViewPartners) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/v1/platform/partners/search?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPartners(data.data || []);
      }
    } catch (err) {
      console.error('Failed to load partners:', err);
    }
    // hasPermission keeps a stable store identity, so depend on the user it reads.
  }, [user, canViewPartners]);

  // Search partners for assign modal
  const searchPartnersForAssign = useCallback(async (query: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setIsSearchingPartners(true);
    try {
      const url = query
        ? `${API_URL}/api/v1/platform/partners/search?q=${encodeURIComponent(query)}&limit=10`
        : `${API_URL}/api/v1/platform/partners/search?limit=20`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPartnerSearchResults(data.data || []);
      }
    } catch (err) {
      console.error('Failed to search partners:', err);
    } finally {
      setIsSearchingPartners(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
    fetchPartners();
  }, [filters.partner, fetchPartners]);

  // Apply search filter and client-side pagination/sorting
  const filteredOrganizations = useMemo(() => {
    let result = [...organizations];

    // Filter by org status tab
    const statusFilter = (filters.orgStatus as string) || 'active';
    if (statusFilter === 'active') {
      result = result.filter(org => !org.isDeleted);
    } else if (statusFilter === 'deactivated') {
      result = result.filter(org => org.isDeleted);
    }

    // Apply search filter
    const searchQuery = (filters.search as string) || '';
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((org) =>
        org.name.toLowerCase().includes(query) ||
        (org.subdomain && org.subdomain.toLowerCase().includes(query)) ||
        (org.adminEmail && org.adminEmail.toLowerCase().includes(query)) ||
        (org.settings.externalTenantId && org.settings.externalTenantId.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    if (sortState.column) {
      result.sort((a, b) => {
        let aVal: string | number | boolean = '';
        let bVal: string | number | boolean = '';

        switch (sortState.column) {
          case 'name':
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case 'subdomain':
            aVal = (a.subdomain || '').toLowerCase();
            bVal = (b.subdomain || '').toLowerCase();
            break;
          case 'users':
            aVal = a.userCount || 0;
            bVal = b.userCount || 0;
            break;
          case 'partner':
            aVal = (a.partnerName || '').toLowerCase();
            bVal = (b.partnerName || '').toLowerCase();
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortState.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortState.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [organizations, filters.search, filters.orgStatus, sortState]);

  // Update pagination total when filtered results change
  useEffect(() => {
    setPagination(prev => ({ ...prev, total: filteredOrganizations.length }));
  }, [filteredOrganizations.length]);

  // Get paginated data
  const paginatedOrganizations = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filteredOrganizations.slice(start, start + pagination.limit);
  }, [filteredOrganizations, pagination.page, pagination.limit]);

  const fetchOrganizations = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      // Build query params — always fetch all orgs (active + deactivated), filter client-side
      const params = new URLSearchParams();
      params.append('includeDeleted', 'true');
      const partnerFilter = filters.partner as string;
      if (partnerFilter) {
        params.append('partnerId', partnerFilter);
      }

      const url = `${API_URL}/api/v1/platform/organizations${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch organizations: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setOrganizations(result.data);
      } else {
        throw new Error(result.error || 'Failed to load organizations');
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrg = (org: Organization) => {
    setModalMode('edit');
    setEditingOrg(org);
    setShowModal(true);
  };

  const handleCreateClick = () => {
    setModalMode('create');
    setEditingOrg(null);
    setShowModal(true);
  };

  const handleSaveOrganization = async (org: Organization) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Authentication required');
    }

    const isCreate = !org.id;
    const url = isCreate
      ? `${API_URL}/api/v1/platform/organizations`
      : `${API_URL}/api/v1/platform/organizations/${org.id}`;

    const method = isCreate ? 'POST' : 'PUT';

    // Flatten settings fields to root level (backend expects operationMode, externalTenantId, etc. at root)
    const payload: any = {
      ...org,
      operationMode: org.operationMode || org.settings?.operationMode || 'standalone',
      externalTenantId: org.externalTenantId || org.settings?.externalTenantId,
      externalSystemId: org.externalSystemId || org.settings?.externalSystemId,
      externalSystemTimezone: org.externalSystemTimezone || org.settings?.externalSystemTimezone,
      // Per-organization feature flags: flatten to root so the backend
      // validator/updateData path picks them up alongside other org fields.
      emCodingAssistantEnabled: org.settings?.emCodingAssistantEnabled,
      cptCodingAssistantEnabled: org.settings?.cptCodingAssistantEnabled,
    };

    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.error || `Failed to ${isCreate ? 'create' : 'update'} organization`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || `Failed to ${isCreate ? 'create' : 'update'} organization`);
    }

    // Reload organizations list
    await fetchOrganizations();

    // If the superadmin happens to be editing their own organization (rare but
    // possible when a superadmin is also seeded as a member), push the flag
    // values into the in-memory store so the clinical UI reflects the save
    // immediately without a re-login.
    const authUser = useAuth.getState().user;
    if (authUser?.organizationId && authUser.organizationId === org.id) {
      useOrganizationFeatureFlags.getState().applyFlags({
        emCodingAssistantEnabled: org.settings?.emCodingAssistantEnabled ?? true,
        cptCodingAssistantEnabled: org.settings?.cptCodingAssistantEnabled ?? true,
      });
    }
  };

  // Initiate deactivation - gets cascade impact for the confirmation modal
  const initiateDeactivate = async (org: Organization) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setError(null);

      const response = await fetch(`${API_URL}/api/v1/platform/organizations/${org.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({}) // No confirmName - just get cascade impact
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Failed to get deactivation info: ${response.status}`);
      }

      if (result.requiresConfirmation) {
        setDeactivatingOrg(org);
        setCascadeImpact(result.cascadeImpact);
        setShowDeactivateModal(true);
      }
    } catch (err) {
      console.error('Error initiating deactivation:', err);
      setError(err instanceof Error ? err.message : 'Failed to initiate deactivation');
    }
  };

  // Execute deactivation with name confirmation
  const handleDeactivateOrg = async (confirmName: string) => {
    if (!deactivatingOrg) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setError(null);
      setIsDeactivating(true);

      const response = await fetch(`${API_URL}/api/v1/platform/organizations/${deactivatingOrg.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirmName })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Failed to deactivate organization: ${response.status}`);
      }

      if (result.success) {
        setShowDeactivateModal(false);
        setDeactivatingOrg(null);
        setCascadeImpact(undefined);

        // Reload organizations list
        fetchOrganizations();
      } else {
        throw new Error(result.error || 'Failed to deactivate organization');
      }
    } catch (err) {
      console.error('Error deactivating organization:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate organization');
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleReactivateOrg = async () => {
    if (!reactivatingOrg) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setError(null);

      const response = await fetch(`${API_URL}/api/v1/platform/organizations/${reactivatingOrg.id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to reactivate organization: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setShowReactivateConfirm(false);
        setReactivatingOrg(null);
        fetchOrganizations(); // Reload list
      } else {
        throw new Error(result.error || 'Failed to reactivate organization');
      }
    } catch (err) {
      console.error('Error reactivating organization:', err);
      setError(err instanceof Error ? err.message : 'Failed to reactivate organization');
    }
  };

  const handleHardDelete = async () => {
    if (!hardDeletingOrg?.id) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setIsHardDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/platform/organizations/${hardDeletingOrg.id}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmName: hardDeleteConfirmName, confirmCheckbox: hardDeleteCheckbox })
      });
      const result = await response.json();
      if (result.success) {
        setShowHardDeleteModal(false);
        setHardDeletingOrg(null);
        setHardDeleteConfirmName('');
        setHardDeleteCheckbox(false);
        fetchOrganizations();
      } else {
        setError(result.error || 'Failed to permanently delete');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to permanently delete organization');
    } finally {
      setIsHardDeleting(false);
    }
  };

  const getModeBadge = (mode: string) => {
    if (mode === 'standalone') {
      return (
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
          Standalone
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
        Connected
      </span>
    );
  };

  const getPartnerTypeBadge = (type: string) => {
    const styles: Record<string, string> = {
      internal: 'bg-purple-100 text-purple-700',
      enterprise: 'bg-blue-100 text-blue-700',
      reseller: 'bg-green-100 text-green-700',
      affiliate: 'bg-gray-100 text-gray-700'
    };
    return styles[type] || 'bg-gray-100 text-gray-700';
  };

  // Filter configuration for FilterBar
  const filterConfig: FilterConfig = useMemo(() => ({
    filters: [
      {
        key: 'search',
        type: 'search' as const,
        label: 'Search',
        placeholder: 'Search by name, subdomain, email...',
        width: 'lg' as const,
        debounceMs: 300,
      },
      // FilterSelect renders the placeholder as its leading option, so an 'All Partners' entry
      // here would be the same choice twice. The list behind it needs platform.partners.view.
      ...(canViewPartners ? [{
        key: 'partner',
        type: 'select' as const,
        label: 'Partner',
        placeholder: 'All Partners',
        width: 'md' as const,
        options: partners.map(p => ({
          value: p.id,
          label: `${p.isOwnerPartner ? '👑 ' : ''}${p.name} (${p.type})`,
        })),
      }] : []),
    ],
    showClearAll: true,
    showActiveChips: true,
  }), [partners, canViewPartners]);

  // DataTable column definitions with explicit widths for table-fixed layout
  const organizationColumns: ColumnDefinition<Organization>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Organization',
      sortable: true,
      width: '22%',
      accessor: (org) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <BuildingOfficeIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="ml-4 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{org.name}</div>
            <div className="text-sm text-gray-500 truncate">@{org.subdomain}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'partner',
      header: 'Partner',
      sortable: true,
      width: '15%',
      accessor: (org) => org.partnerName ? (
        <div className="flex items-center gap-2 min-w-0">
          {org.partnerIsOwner && (
            <span title="Owner Partner" className="flex-shrink-0"><Crown className="h-4 w-4 text-amber-500" /></span>
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{org.partnerName}</div>
            {org.partnerType && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPartnerTypeBadge(org.partnerType)}`}>
                {org.partnerType}
              </span>
            )}
          </div>
        </div>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <ExclamationCircleIcon className="h-3 w-3 mr-1" />
          No Partner
        </span>
      ),
    },
    {
      id: 'mode',
      header: 'Mode',
      sortable: true,
      sortKey: 'settings.operationMode',
      width: '15%',
      hideOnTablet: true,
      accessor: (org) => (
        <div className="space-y-1 min-w-0">
          {getModeBadge(org.settings.operationMode)}
          {org.settings.operationMode === 'connected' && (
            <div className="text-xs text-gray-600 mt-1 space-y-0.5">
              {org.settings.externalSystems && org.settings.externalSystems.length > 0 ? (
                org.settings.externalSystems.slice(0, 1).map((sys, idx) => (
                  <div key={idx} className="flex items-center space-x-1 truncate">
                    <span className="font-medium truncate">{sys.displayName || sys.systemId}</span>
                    {sys.isPrimary && (
                      <span className="text-purple-600 text-xs flex-shrink-0">(Primary)</span>
                    )}
                  </div>
                ))
              ) : (
                <span className="text-gray-400">No system</span>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'users',
      header: 'Users',
      sortable: true,
      width: '8%',
      accessor: (org) => (
        <div className="flex items-center text-sm text-gray-900">
          <UserGroupIcon className="h-4 w-4 mr-1 text-gray-400 flex-shrink-0" />
          {org.userCount || 0}
        </div>
      ),
    },
    {
      id: 'plan',
      header: 'Plan',
      width: '10%',
      hideOnTablet: true,
      // Read-only for every role. Roles that cannot manage subscriptions still need to see which
      // plan an organization is on, and an organization with none is unusable (its users get 402),
      // so "None" is the row worth noticing rather than a blank.
      accessor: (org) => {
        const planName = org.subscription?.planName;
        return planName ? (
          <span className="text-sm text-gray-900">{planName}</span>
        ) : (
          <span className="text-sm text-amber-700" title="No subscription: recording, encounters and AI features return 402 for everyone in this organization until a plan is assigned">
            None
          </span>
        );
      },
    },
    {
      id: 'adminEmail',
      header: 'Admin Email',
      sortable: true,
      sortKey: 'adminEmail',
      width: '18%',
      hideOnTablet: true,
      accessor: (org) => (
        <span className="text-sm text-gray-500 truncate block" title={org.adminEmail}>
          {org.adminEmail}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'isDeleted',
      width: '12%',
      accessor: (org) => (
        <div className="flex flex-col gap-1">
          {!org.isDeleted ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircleIcon className="h-3 w-3 mr-1" />
              Active
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <XCircleIcon className="h-3 w-3 mr-1" />
              Inactive
            </span>
          )}
        </div>
      ),
    },
  ], []);

  // DataTable action definitions
  // Each control mirrors the rule its own endpoint enforces, via the shared table. Not the
  // permission the role happens to hold: a channel partner admin has platform.organizations.create
  // while the create route admits superadmin and enterprise_admin by role, so a permission-based
  // gate on New Organization would show a button the server refuses.
  const allowed = (action: ConsoleAction) => canUseConsoleAction(action, { role: user?.role, hasPermission });

  const organizationActions: ActionDefinition<Organization>[] = useMemo(() => [
    {
      id: 'edit',
      icon: <PencilIcon className="h-4 w-4" />,
      tooltip: 'Edit organization',
      onClick: (org) => handleEditOrg(org),
      show: (org) => !org.isDeleted && allowed('editOrganization'),
      variant: 'primary',
    },
    {
      id: 'viewUsers',
      icon: <UserGroupIcon className="h-4 w-4" />,
      tooltip: 'View users',
      onClick: (org) => {
        window.location.href = `/management/users?organizationId=${org.id}`;
      },
      show: (org) => !org.isDeleted && allowed('viewOrganizationUsers'),
      variant: 'default',
    },
    {
      id: 'manageSubscription',
      icon: <CreditCardIcon className="h-4 w-4" />,
      tooltip: 'Manage subscription',
      onClick: (org) => {
        setSubscriptionOrg(org);
        setShowSubscriptionModal(true);
      },
      show: (org) => !org.isDeleted && allowed('manageSubscription'),
      variant: 'default',
    },
    {
      id: 'importUsers',
      icon: <ArrowUpTrayIcon className="h-4 w-4" />,
      tooltip: 'Import users (CSV)',
      onClick: (org) => {
        setImportingOrg(org);
        setShowImportModal(true);
      },
      show: (org) => !org.isDeleted && allowed('importUsers'),
      variant: 'default',
    },
    {
      id: 'assignPartner',
      icon: <LinkIcon className="h-4 w-4" />,
      tooltip: 'Assign partner',
      onClick: (org) => handleOpenAssignPartner(org),
      show: (org) => !org.isDeleted && !org.partnerId && allowed('assignPartner'),
      variant: 'warning',
    },
    {
      id: 'deactivate',
      icon: <PowerIcon className="h-4 w-4" />,
      tooltip: 'Deactivate organization',
      onClick: (org) => initiateDeactivate(org),
      show: (org) => !org.isDeleted && allowed('deactivateOrganization'),
      variant: 'danger',
    },
    {
      id: 'reactivate',
      icon: <ArrowPathIcon className="h-4 w-4" />,
      tooltip: 'Reactivate organization',
      onClick: (org) => {
        setReactivatingOrg(org);
        setShowReactivateConfirm(true);
      },
      show: (org) => org.isDeleted === true && allowed('reactivateOrganization'),
      variant: 'success',
    },
    {
      id: 'permanentDelete',
      icon: <TrashIcon className="h-4 w-4" />,
      tooltip: 'Permanently delete organization',
      onClick: (org) => {
        setHardDeletingOrg(org);
        setHardDeleteConfirmName('');
        setHardDeleteCheckbox(false);
        setShowHardDeleteModal(true);
      },
      show: (org) => org.isDeleted === true && allowed('permanentlyDeleteOrganization'),
      variant: 'danger',
    },
    // hasPermission keeps a stable store identity, so depend on the user it reads.
  ], [initiateDeactivate, user]);

  // Open assign partner modal
  const handleOpenAssignPartner = (org: Organization) => {
    setAssigningOrg(org);
    setPartnerSearchQuery('');
    setShowAssignPartnerModal(true);
    searchPartnersForAssign('');
  };

  // Bulk action handlers
  const handleBulkActivate = useCallback(async (ids: string[]) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      // fetch does not reject on 4xx, so without this the refusal was invisible: the list refreshed
      // unchanged and nothing was reported.
      const responses = await Promise.all(ids.map(id =>
        fetch(`${API_URL}/api/v1/platform/organizations/${id}/reactivate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ));
      const failed = responses.filter(r => !r.ok).length;
      setSelectedIds(new Set());
      fetchOrganizations();
      if (failed > 0) {
        setError(`Failed to reactivate ${failed} of ${ids.length} organization(s)`);
      }
    } catch (err) {
      console.error('Failed to reactivate organizations:', err);
      setError('Failed to reactivate organizations');
    }
  }, []);

  const handleBulkDeactivate = useCallback(async (ids: string[]) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // Bulk deactivation requires name confirmation per org — use individual deactivation
    // For simplicity, iterate and confirm each one
    try {
      let failed = 0;
      for (const id of ids) {
        const org = organizations.find(o => o.id === id);
        if (!org) continue;
        const response = await fetch(`${API_URL}/api/v1/platform/organizations/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ confirmName: org.name })
        });
        if (!response.ok) failed++;
      }
      setSelectedIds(new Set());
      fetchOrganizations();
      if (failed > 0) {
        setError(`Failed to deactivate ${failed} of ${ids.length} organization(s)`);
      }
    } catch (err) {
      console.error('Failed to deactivate organizations:', err);
      setError('Failed to deactivate organizations');
    }
  }, [organizations]);

  // Bulk actions configuration
  const bulkActions: BulkAction<Organization>[] = useMemo(() => [
    {
      id: 'activate',
      label: 'Activate',
      icon: <PlayIcon className="h-4 w-4" />,
      variant: 'success',
      onClick: handleBulkActivate,
      // Gated on the same rules as the matching row action. Without this the bulk bar offered three
      // buttons whose endpoints are superadmin-only, and the handlers below ignored the refusal, so
      // the list simply refreshed unchanged and the failure looked like success.
      show: (selectedItems) => selectedItems.some(o => o.isDeleted) && allowed('reactivateOrganization'),
    },
    {
      id: 'deactivate',
      label: 'Deactivate',
      icon: <PowerIcon className="h-4 w-4" />,
      variant: 'warning',
      onClick: handleBulkDeactivate,
      show: (selectedItems) => selectedItems.some(o => !o.isDeleted) && allowed('deactivateOrganization'),
    },
    // hasPermission keeps a stable store identity, so depend on the user it reads.
  ], [handleBulkActivate, handleBulkDeactivate, user]);

  // Assign partner to organization
  const handleAssignPartner = async (partnerId: string) => {
    if (!assigningOrg) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setIsAssigningPartner(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/platform/organizations/${assigningOrg.id}/assign-partner`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ partnerId })
      });

      const result = await response.json();
      if (result.success) {
        setShowAssignPartnerModal(false);
        setAssigningOrg(null);
        fetchOrganizations();
      } else {
        throw new Error(result.error || 'Failed to assign partner');
      }
    } catch (err) {
      console.error('Error assigning partner:', err);
      setError(err instanceof Error ? err.message : 'Failed to assign partner');
    } finally {
      setIsAssigningPartner(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage customer organizations and their settings
          </p>
        </div>
        {allowed('createOrganization') && (
          <button
            onClick={handleCreateClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Organization
          </button>
        )}
      </div>

      {/* FilterBar */}
      <div className="bg-white shadow rounded-lg p-4">
        <FilterBar
          config={filterConfig}
          values={filters}
          onChange={(key, value) => setFilter(key as keyof typeof filters, value as never)}
          onClear={(key) => clearFilter(key as keyof typeof filters)}
          onClearAll={clearAllFilters}
          activeFilters={activeFilters}
          loading={loading}
        />
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { value: 'active', label: 'Active', count: organizations.filter(o => !o.isDeleted).length },
          { value: 'deactivated', label: 'Deactivated', count: organizations.filter(o => o.isDeleted).length },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilter('orgStatus' as keyof typeof filters, tab.value as never)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              (filters.orgStatus || 'active') === tab.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                (filters.orgStatus || 'active') === tab.value
                  ? tab.value === 'deactivated' ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <BulkActionsBar
            selectedIds={selectedIds}
            items={filteredOrganizations}
            getItemId={(o) => o.id || ''}
            actions={bulkActions}
            onClearSelection={() => setSelectedIds(new Set())}
            entityName="organization"
            theme="purple"
          />
        )}
      </AnimatePresence>

      {/* Organizations Table */}
      <DataTable
        data={paginatedOrganizations}
        columns={organizationColumns}
        actions={organizationActions}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        sortable={true}
        sortState={sortState}
        onSortChange={setSortState}
        striped={true}
        hoverable={true}
        className="shadow rounded-lg"
        getRowId={(org) => org.id || ''}
        ariaLabel="Organizations table"
        emptyMessage="No organizations"
        emptyIcon={<BuildingOfficeIcon className="h-12 w-12 text-gray-400" />}
        pagination={pagination}
        onPageChange={(page) => setPagination(p => ({ ...p, page }))}
        onLimitChange={(limit) => setPagination(p => ({ ...p, limit, page: 1 }))}
      />

      {/* Empty State */}
      {!loading && paginatedOrganizations.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {(filters.search as string)?.trim() ? 'No organizations found' : 'No organizations'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {(filters.search as string)?.trim()
              ? 'Try adjusting your search query.'
              : 'Get started by creating a new organization.'}
          </p>
          <div className="mt-6">
            {(filters.search as string)?.trim() ? (
              <button
                onClick={() => clearFilter('search')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
              >
                Clear Search
              </button>
            ) : allowed('createOrganization') ? (
              <button
                onClick={handleCreateClick}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                New Organization
              </button>
            ) : (
              // Nothing to offer: this role cannot create, and the empty-state copy above already
              // explains the list is empty.
              <></>
            )}
          </div>
        </div>
      )}

      {/* Organization Modal (Create/Edit) */}
      <OrganizationModal
        mode={modalMode}
        organization={editingOrg}
        open={showModal}
        onOpenChange={setShowModal}
        onSave={handleSaveOrganization}
      />

      {/* Deactivate Confirmation Modal */}
      <DeactivateConfirmModal
        isOpen={showDeactivateModal}
        entityType="organization"
        entityName={deactivatingOrg?.name || ''}
        cascadeImpact={cascadeImpact}
        isLoading={isDeactivating}
        onConfirm={handleDeactivateOrg}
        onCancel={() => {
          setShowDeactivateModal(false);
          setDeactivatingOrg(null);
          setCascadeImpact(undefined);
          setError(null);
        }}
      />

      {/* Reactivate Confirmation Dialog */}
      <ConfirmationDialog
        open={showReactivateConfirm}
        onOpenChange={setShowReactivateConfirm}
        title="Reactivate Organization"
        message={
          reactivatingOrg ? (
            <>
              <p className="mb-2">
                Are you sure you want to reactivate <strong>{reactivatingOrg.name}</strong>?
              </p>
              <p className="text-sm text-gray-600">
                This will restore users that were deactivated when this organization was deleted.
              </p>
            </>
          ) : ''
        }
        confirmText="Reactivate"
        cancelText="Cancel"
        onConfirm={handleReactivateOrg}
        variant="info"
      />

      {/* Hard Delete Confirmation Modal */}
      {showHardDeleteModal && hardDeletingOrg && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowHardDeleteModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all">
              <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50 rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                    <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-red-900">Permanently Delete Organization</h2>
                </div>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>This action is permanent and irreversible.</strong> All data including users, patients,
                    appointments, clinical encounters, recordings, and medical notes will be permanently deleted.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm text-gray-700">
                    To confirm, type{' '}
                    <span className="font-mono font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{hardDeletingOrg.name}</span>
                    {' '}below:
                  </label>
                  <input
                    type="text"
                    value={hardDeleteConfirmName}
                    onChange={(e) => setHardDeleteConfirmName(e.target.value)}
                    placeholder="Type organization name to confirm"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    autoFocus
                    disabled={isHardDeleting}
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hardDeleteCheckbox}
                    onChange={(e) => setHardDeleteCheckbox(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    disabled={isHardDeleting}
                  />
                  <span className="text-sm text-gray-700">
                    I understand this will permanently delete all organization data and this action cannot be undone
                  </span>
                </label>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <Button variant="outline" onClick={() => setShowHardDeleteModal(false)} disabled={isHardDeleting}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={handleHardDelete}
                  disabled={hardDeleteConfirmName !== hardDeletingOrg.name || !hardDeleteCheckbox || isHardDeleting}
                >
                  {isHardDeleting ? 'Deleting...' : 'I understand the consequences, delete this organization'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Partner Modal */}
      <Dialog open={showAssignPartnerModal} onOpenChange={setShowAssignPartnerModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-purple-600" />
              Assign Partner to Organization
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {assigningOrg && (
              <p className="text-sm text-gray-600 mb-4">
                Select a partner for <strong>{assigningOrg.name}</strong>
              </p>
            )}

            {/* Partner Search */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                value={partnerSearchQuery}
                onChange={(e) => {
                  setPartnerSearchQuery(e.target.value);
                  searchPartnersForAssign(e.target.value);
                }}
                placeholder="Search partners by name..."
                className="pl-10"
              />
            </div>

            {/* Partner List */}
            <div className="max-h-64 overflow-y-auto border rounded-md">
              {isSearchingPartners ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                </div>
              ) : partnerSearchResults.length > 0 ? (
                <div className="divide-y">
                  {partnerSearchResults.map((partner) => (
                    <button
                      key={partner.id}
                      onClick={() => handleAssignPartner(partner.id)}
                      disabled={isAssigningPartner}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none disabled:opacity-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {partner.isOwnerPartner && (
                            <Crown className="h-4 w-4 text-amber-500" />
                          )}
                          <div>
                            <div className="font-medium text-gray-900">{partner.name}</div>
                            <div className="text-xs text-gray-500">{partner.slug}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPartnerTypeBadge(partner.type)}`}>
                            {partner.type}
                          </span>
                          {partner.quotaUsage && !partner.quotaUsage.isUnlimited && (
                            <div className="text-xs text-gray-500 mt-1">
                              {partner.quotaUsage.organizations}/{partner.quotaUsage.maxOrganizations} orgs
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No partners found</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setShowAssignPartnerModal(false)}
              disabled={isAssigningPartner}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Subscription Modal */}
      {subscriptionOrg && (
        <ManageSubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => {
            setShowSubscriptionModal(false);
            setSubscriptionOrg(null);
          }}
          organization={subscriptionOrg}
          onSuccess={() => fetchOrganizations()}
        />
      )}

      {/* Bulk User Import Modal */}
      {importingOrg && (
        <BulkUserImportModal
          isOpen={showImportModal}
          onClose={() => {
            setShowImportModal(false);
            setImportingOrg(null);
          }}
          organizationId={importingOrg.id || ''}
          organizationName={importingOrg.name}
          onImportComplete={() => fetchOrganizations()}
        />
      )}
    </div>
  );
};

export default OrganizationsList;
